import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Calendar, MapPin, Printer, Ticket, KeyRound, FileClock, UserRound, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PublicHeader } from "@/components/PublicHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Panel, BrandButton, StatusPill } from "@/components/PageHeader";
import { ReceiptView } from "@/pages/Ledger";
import { StandingCard } from "@/components/member/StandingCard";
import { useApp } from "@/context/AppContext";
import { api, cedi, fmtDate, showPin, balanceOf, overpaymentOf } from "@/lib/api";
import { EditRegistrationButton } from "@/components/event-registrations/EditRegistrationDialog";

const hasPayment = (t) => (t.payments || []).length > 0 || (t.paid || 0) > 0;

const EditOrLock = ({ t, onChanged }) => hasPayment(t)
  ? <span className="text-[11px] text-gray-500 inline-flex items-center gap-1" title="A payment has been recorded. Contact the GAMLS administrators to change this registration." data-testid={`my-edit-locked-${t.ticket_no}`}><Lock className="w-3 h-3" />Edit locked</span>
  : <EditRegistrationButton r={t} member onChanged={onChanged} />;

const Registrations = ({ items, onView, onChanged }) => (
  <Panel title="My registrations" testId="my-registrations">
    {items.length === 0 ? <p className="text-sm text-gray-500" data-testid="my-registrations-empty">You have not registered for an event yet.</p> : (
      <ul className="divide-y -my-2">
        {items.map((t) => (
          <li key={t.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between" data-testid={`my-registration-${t.ticket_no}`}>
            <div>
              <div className="font-semibold">{t.event_title}</div>
              <div className="text-xs text-gray-500">{t.category_name || t.category}{t.ticket_no && <> · Ticket <span className="font-mono font-semibold text-[#E60000]" data-testid={`my-ticket-${t.ticket_no}`}>{t.ticket_no}</span></>} · {fmtDate(t.created_at)}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm font-semibold" data-testid={`my-total-${t.ticket_no}`}>{cedi(t.amount)}{overpaymentOf(t) > 0 ? <span className="block text-[11px] text-amber-800" data-testid={`my-overpayment-${t.ticket_no}`}>Overpayment <span className="whitespace-nowrap">{cedi(overpaymentOf(t))}</span> · contact Accounts</span> : balanceOf(t) > 0 && <span className="block text-[11px] font-normal text-gray-500" data-testid={`my-balance-${t.ticket_no}`}>Balance <span className="whitespace-nowrap">{cedi(balanceOf(t))}</span> · pay at event</span>}</span>
              <StatusPill status={t.status} />
              {!t.event_archived && <EditOrLock t={t} onChanged={onChanged} />}
              <button onClick={() => onView(t)} className="text-xs font-semibold text-[#E60000] hover:underline" data-testid={`my-receipt-${t.ticket_no}`}>Receipt</button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

const Events = ({ events, registered }) => (
  <Panel title="Upcoming events" testId="my-events">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {events.map((e) => (
        <div key={e.id} className="border border-[#E5E7EB] rounded-lg p-4 flex flex-col gap-2" data-testid={`my-event-${e.id}`}>
          <div className="font-heading font-bold leading-snug">{e.title}</div>
          <div className="text-xs text-gray-600 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{e.date}</div>
          <div className="text-xs text-gray-600 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{e.venue}</div>
          {registered.has(e.id) ? <span className="text-xs font-semibold text-emerald-700 mt-1">You are registered</span> : (
            <Link to={`/event-registration?event=${e.id}`} className="mt-1 h-9 px-3 w-fit inline-flex items-center gap-2 rounded-md bg-[#E60000] hover:bg-[#CC0000] text-white text-sm font-semibold transition-colors" data-testid={`my-register-${e.id}`}><Ticket className="w-4 h-4" />Register</Link>
          )}
        </div>
      ))}
    </div>
  </Panel>
);

export default function MyEventHub() {
  const { auth, member, events, loggedOut } = useApp();
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  const memberId = member?.id;
  const load = () => api.get("/members/me/registrations").then((r) => setData(r.data));
  useEffect(() => {
    if (memberId) { load(); }
  }, [memberId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (auth === null) return <div className="min-h-screen grid place-items-center text-gray-500">Loading…</div>;
  if (!member) return <Navigate to={loggedOut.current ? "/" : "/login"} replace />;
  if (!member.email_verified) return <Navigate to="/member/verify" replace />;
  if (!member.profile_complete) return <Navigate to="/my/profile" replace />;
  const regs = data?.registrations || [];
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="my-eventhub-page">
      <PublicHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E60000]">My EventHub</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1" data-testid="my-name">Welcome, {member.first_name || member.name}</h1>
            <p className="text-sm text-gray-500 mt-1 font-mono">{showPin(member.pin)} · {member.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/my/profile" className="text-sm font-semibold inline-flex items-center gap-1.5 hover:text-[#E60000]" data-testid="my-edit-profile"><UserRound className="w-4 h-4" />My profile</Link>
            <Link to="/change-password" className="text-sm font-semibold inline-flex items-center gap-1.5 hover:text-[#E60000]" data-testid="my-change-password"><KeyRound className="w-4 h-4" />Change password</Link>
          </div>
        </div>
        <StandingCard member={member} />
        {data?.draft && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="my-draft-banner">
            <div className="flex items-center gap-2 text-sm"><FileClock className="w-5 h-5 text-amber-600" />You have a saved registration draft from {fmtDate(data.draft.updated_at)}.</div>
            <Link to={`/event-registration?event=${data.draft.event_id || ""}`} className="text-sm font-semibold text-[#E60000] hover:underline" data-testid="my-draft-continue">Continue registration</Link>
          </div>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          <Registrations items={regs} onView={setView} onChanged={load} />
          <Events events={events} registered={new Set(regs.map((t) => t.event_id))} />
        </div>
      </main>
      <SiteFooter />
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="my-receipt-dialog">
          <DialogHeader><DialogTitle className="sr-only">Receipt</DialogTitle></DialogHeader>
          {view && <ReceiptView t={view} />}
          <BrandButton variant="dark" onClick={() => window.print()} data-testid="my-receipt-print"><Printer className="w-4 h-4" />Print receipt</BrandButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}
